/*
Copyright (C) 2023-2026 QuantumNous

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.

For commercial licensing, please contact support@quantumnous.com
*/
import { createFileRoute, useNavigate, useParams, useSearch } from '@tanstack/react-router';
import i18next from 'i18next';
import { useEffect } from 'react';
import { toast } from 'sonner';
import { OAuthCallbackScreen } from '@/features/auth/components/oauth-callback-screen';
import { OAUTH_BIND_CALLBACK_MESSAGE, OAUTH_BIND_RESULT_MESSAGE } from '@/features/auth/constants';
import { sanitizeAuthRedirect } from '@/features/auth/lib/auth-redirect';
import { parseTelegramBindCallback, postTelegramBindResult, startOAuthBindResponseDeadline } from '@/features/auth/lib/oauth-bind-window';
import { getOAuthSessionStorage, resolveOAuthCallbackMode } from '@/features/auth/lib/oauth-callback-mode';
import { api, applyAuthBundle, isAuthBundle } from '@/lib/api';
import { getServerErrorMessageKey } from '@/lib/server-error-message';
type OAuthRequestConfig = AxiosRequestConfig & {
  skipBusinessError?: boolean;
};
interface OAuthBindingResult {
  type: typeof OAUTH_BIND_RESULT_MESSAGE;
  provider: string;
  state: string;
  success: boolean;
  message?: string;
}
function OAuthCallback() {
  const navigate = useNavigate();
  const {
    provider
  } = useParams({
    from: '/oauth/$provider'
  }) as {
    provider: string;
  };
  const search = useSearch({
    from: '/oauth/$provider'
  }) as {
    code?: string;
    state?: string;
    error?: string;
    error_description?: string;
    redirect?: string;
    telegram_bind?: string;
    flow_token?: string;
    error_code?: string;
  };
  const callbackState = search.state ?? '';
  const isTelegramBindCallback = provider === 'telegram' && (search.telegram_bind === 'success' || search.telegram_bind === 'error');
  let mode: 'login' | 'bind' = 'login';
  if (isTelegramBindCallback) {
    mode = 'bind';
  } else if (typeof window !== 'undefined') {
    mode = resolveOAuthCallbackMode(provider, callbackState, {
      opener: window.opener,
      storage: getOAuthSessionStorage(window)
    });
  }
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const code = search.code ?? '';
    const state = callbackState;
    const telegramCallback = provider === 'telegram' ? parseTelegramBindCallback({
      telegram_bind: search.telegram_bind,
      flow_token: search.flow_token,
      error_code: search.error_code
    }) : null;
    if (telegramCallback) {
      const opener = window.opener;
      if (!postTelegramBindResult(telegramCallback, opener, window.location.origin)) {
        toast.error(i18next.t('Telegram binding failed. Please try again.'));
        const closeTimeout = window.setTimeout(() => window.close(), 1500);
        return () => window.clearTimeout(closeTimeout);
      }
      window.close();
      return;
    }
    if (mode === 'bind') {
      const opener = window.opener;
      if (!opener || opener.closed) {
        toast.error(i18next.t('OAuth binding window is no longer available'));
        return;
      }
      let cancelResultTimeout: () => void = () => undefined;
      let delayedClose: number | undefined;
      const handleBindingResult = (event: MessageEvent<unknown>) => {
        if (event.origin !== window.location.origin || event.source !== opener) {
          return;
        }
        const result = event.data as Partial<OAuthBindingResult> | null;
        if (!result || result.type !== OAUTH_BIND_RESULT_MESSAGE || result.provider !== provider || result.state !== state) {
          return;
        }
        cancelResultTimeout();
        if (result.success) {
          toast.success(i18next.t('Binding successful!'));
          window.close();
          return;
        }
        toast.error(result.message || i18next.t('OAuth failed'));
        delayedClose = window.setTimeout(() => window.close(), 1500);
      };
      window.addEventListener('message', handleBindingResult);
      cancelResultTimeout = startOAuthBindResponseDeadline(() => {
        toast.error(i18next.t('OAuth binding timed out. Please try again.'));
        delayedClose = window.setTimeout(() => window.close(), 1500);
      });
      opener.postMessage({
        type: OAUTH_BIND_CALLBACK_MESSAGE,
        provider,
        code,
        state,
        error: search.error,
        errorDescription: search.error_description
      }, window.location.origin);
      return () => {
        window.removeEventListener('message', handleBindingResult);
        cancelResultTimeout();
        if (delayedClose !== undefined) window.clearTimeout(delayedClose);
      };
    }
    const safeNavigate = (target: unknown, fallback = '/dashboard') => {
      const href = sanitizeAuthRedirect(target, window.location.origin) ?? fallback;
      void navigate({
        href,
        replace: true
      });
    };
    if (!code && !search.error) {
      toast.error(i18next.t('Missing code'));
      safeNavigate('/sign-in', '/sign-in');
      return;
    }
    void (async () => {
      try {
        const config: OAuthRequestConfig = {
          params: {
            code: code || undefined,
            state,
            error: search.error,
            error_description: search.error_description
          },
          skipBusinessError: true
        };
        const response = await api.get(`/api/oauth/${provider}`, config);
        if (response.data?.success && isAuthBundle(response.data?.data)) {
          applyAuthBundle(response.data.data);
          safeNavigate(search.redirect);
          toast.success(i18next.t('Signed in successfully!'));
          return;
        }
        const messageKey = getServerErrorMessageKey(response.data);
        toast.error(messageKey ? i18next.t(messageKey) : response.data?.message || i18next.t('OAuth failed'));
      } catch (error: unknown) {
        const messageKey = getServerErrorMessageKey(error);
        const responseMessage = (error as {
          response?: {
            data?: {
              message?: string;
            };
          };
        }).response?.data?.message;
        if (!messageKey) {
          toast.error(responseMessage || (error instanceof Error ? error.message : i18next.t('OAuth failed')));
        }
      }
      safeNavigate('/sign-in', '/sign-in');
    })();
  }, [callbackState, mode, navigate, provider, search.code, search.error, search.error_code, search.error_description, search.flow_token, search.redirect, search.telegram_bind]);
  return <OAuthCallbackScreen provider={provider} mode={mode} />;
}
export const Route = createFileRoute('/oauth/$provider')({
  component: OAuthCallback
});
if (import.meta.webpackHot) {
  const hot = import.meta.webpackHot;
  const hotData = hot.data ??= {};
  const routeId = hotData['tsr-route-id'] ?? Route.id ?? (Route.isRoot ? '__root__' : "/oauth/$provider");
  if (routeId) {
    hotData['tsr-route-id'] = routeId;
  }
  const existingRoute = typeof window !== 'undefined' && routeId ? window.__TSR_ROUTER__?.routesById?.[routeId] : undefined;
  if (routeId && existingRoute && existingRoute !== Route) {
    (function handleRouteUpdate(routeId, newRoute) {
      const router = window.__TSR_ROUTER__,
        oldRoute = router.routesById[routeId];
      if (!oldRoute) return;
      const generatedRouteOptionKeys = new Set(["id", "path", "getParentRoute"]),
        generatedRouteOptions = {};
      generatedRouteOptionKeys.forEach(key => {
        if (key in oldRoute.options) generatedRouteOptions[key] = oldRoute.options[key];
      });
      const removedKeys = new Set();
      Object.keys(oldRoute.options).forEach(key => {
        if (!generatedRouteOptionKeys.has(key) && !(key in newRoute.options)) {
          removedKeys.add(key);
          delete oldRoute.options[key];
        }
      });
      const preserveComponentIdentity = "shellComponent" in oldRoute.options === "shellComponent" in newRoute.options,
        componentKeys = ["component", "shellComponent", "pendingComponent", "errorComponent", "notFoundComponent"];
      if (preserveComponentIdentity) componentKeys.forEach(key => {
        if (key in oldRoute.options && key in newRoute.options) newRoute.options[key] = oldRoute.options[key];
      });
      const nextOptions = {
        ...newRoute.options,
        ...generatedRouteOptions
      };
      oldRoute.options = nextOptions;
      oldRoute.update(nextOptions);
      oldRoute._componentsPromise = void 0;
      oldRoute._lazyPromise = void 0;
      router.setRoutes(router.buildRouteTree());
      syncHotRouteExport(oldRoute);
      router.resolvePathCache.clear();
      const filter = m => m.routeId === oldRoute.id,
        activeMatch = router.stores.matches.get().find(filter),
        pendingMatch = router.stores.pendingMatches.get().find(filter),
        cachedMatches = router.stores.cachedMatches.get().filter(filter);
      if (activeMatch || pendingMatch || cachedMatches.length > 0) {
        if (removedKeys.has("loader") || removedKeys.has("beforeLoad")) {
          const matchIds = [activeMatch?.id, pendingMatch?.id, ...cachedMatches.map(match => match.id)].filter(Boolean);
          router.batch(() => {
            for (const matchId of matchIds) {
              const store = router.stores.pendingMatchStores.get(matchId) || router.stores.matchStores.get(matchId) || router.stores.cachedMatchStores.get(matchId);
              if (store) store.set(prev => {
                const next = {
                  ...prev
                };
                if (removedKeys.has("loader")) next.loaderData = void 0;
                if (removedKeys.has("beforeLoad")) {
                  next.__beforeLoadContext = void 0;
                  next.context = rebuildMatchContextWithoutBeforeLoad(next);
                }
                return next;
              });
            }
          });
        }
        router.invalidate({
          filter,
          sync: !0
        });
      }
      function syncHotRouteExport(liveRoute) {
        newRoute.options = liveRoute.options;
        newRoute.parentRoute = liveRoute.parentRoute;
        newRoute._path = liveRoute._path;
        newRoute._id = liveRoute._id;
        newRoute._fullPath = liveRoute._fullPath;
        newRoute._to = liveRoute._to;
      }
      function getStoreMatch(matchId) {
        return router.stores.pendingMatchStores.get(matchId)?.get() || router.stores.matchStores.get(matchId)?.get() || router.stores.cachedMatchStores.get(matchId)?.get();
      }
      function getMatchList(matchId) {
        const pendingMatches = router.stores.pendingMatches.get();
        if (pendingMatches.some(match => match.id === matchId)) return pendingMatches;
        const activeMatches = router.stores.matches.get();
        if (activeMatches.some(match => match.id === matchId)) return activeMatches;
        const cachedMatches = router.stores.cachedMatches.get();
        if (cachedMatches.some(match => match.id === matchId)) return cachedMatches;
        return [];
      }
      function getParentMatch(match) {
        const matchList = getMatchList(match.id),
          matchIndex = matchList.findIndex(item => item.id === match.id);
        if (matchIndex <= 0) return;
        const parentMatch = matchList[matchIndex - 1];
        return getStoreMatch(parentMatch.id) || parentMatch;
      }
      function rebuildMatchContextWithoutBeforeLoad(match) {
        const parentMatch = getParentMatch(match),
          getParentContext = router.getParentContext;
        return {
          ...((getParentContext ? getParentContext.call(router, parentMatch) : parentMatch?.context ?? router.options.context) ?? {}),
          ...(match.__routeContext ?? {})
        };
      }
    })(routeId, Route);
    try {
      const tsrReactRefreshUtils = typeof __react_refresh_utils__ !== 'undefined' ? __react_refresh_utils__ : undefined;
      const tsrEnqueueUpdate = tsrReactRefreshUtils && typeof tsrReactRefreshUtils.enqueueUpdate === 'function' ? tsrReactRefreshUtils.enqueueUpdate : undefined;
      if (tsrEnqueueUpdate) {
        tsrEnqueueUpdate(() => {});
      }
    } catch (_err) {}
  }
  hot.dispose(data => {
    if (routeId) {
      data['tsr-route-id'] = routeId;
    }
  });
  hot.accept();
}